import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  private apiUrl = `${API_BASE_URL}/documents`;

  constructor(private http: HttpClient) {}

  createDocument(payload: FormData): Observable<any> {
    return this.http.post(this.apiUrl, payload);
  }

  listDocuments(tenantId: number): Observable<any[]> {
    return this.http.get<any[]>(this.apiUrl, { params: { tenant_id: tenantId } });
  }

  dispatchDocument(payload: {
    tenant_id: number;
    document_id: number;
    sent_type: 'ALL_USERS' | 'SPECIFIC_USERS' | 'EMAIL';
    sent_by: number;
    user_ids?: number[];
    recipient_emails?: string[];
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/dispatch`, payload);
  }

  deleteDocument(documentId: number, tenantId: number, userId?: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${documentId}`, {
      params: {
        tenant_id: tenantId,
        ...(userId ? { user_id: userId } : {})
      }
    });
  }

  deleteDispatch(dispatchId: number, tenantId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/dispatches/${dispatchId}`, {
      params: { tenant_id: tenantId }
    });
  }

  getDispatches(documentId: number, tenantId: number, page = 1, pageSize = 5): Observable<{ items: any[]; total: number }> {
    return this.http.get<{ items: any[]; total: number }>(`${this.apiUrl}/${documentId}/dispatches`, {
      params: {
        tenant_id: String(tenantId),
        page: String(page),
        page_size: String(pageSize)
      }
    });
  }

  getOverview(tenantId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/overview`, {
      params: { tenant_id: tenantId }
    });
  }

  getRecipientsList(params: {
    tenant_id: number;
    status?: 'PENDING' | 'DONE';
    document_id?: number;
    dispatch_id?: number;
    search_document?: string;
    search_user?: string;
  }): Observable<any[]> {
    const query: Record<string, string> = { tenant_id: String(params.tenant_id) };
    if (params.status) {
      query['status'] = params.status;
    }
    if (params.document_id) {
      query['document_id'] = String(params.document_id);
    }
    if (params.dispatch_id) {
      query['dispatch_id'] = String(params.dispatch_id);
    }
    if (params.search_document) {
      query['search_document'] = params.search_document;
    }
    if (params.search_user) {
      query['search_user'] = params.search_user;
    }
    return this.http.get<any[]>(`${this.apiUrl}/recipients-list`, { params: query });
  }

  getPendingDocuments(userId: number, tenantId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/pending`, {
      params: { user_id: userId, tenant_id: tenantId }
    });
  }

  getPublicDocuments(tenantId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/public-list`, {
      params: { tenant_id: tenantId }
    });
  }

  publishPublicDocument(documentId: number, payload: { tenant_id: number; user_id: number }): Observable<any> {
    return this.http.post(`${this.apiUrl}/${documentId}/publish-public`, payload);
  }

  completeRecipient(recipientId: number, payload: {
    actions: string[];
    signature_image_path?: string;
    signature_hash?: string;
    selfie_data_url?: string;
    geo?: {
      latitude?: number;
      longitude?: number;
      accuracy?: number;
      city?: string;
      state?: string;
      country?: string;
      provider?: string;
      source?: string;
    };
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/recipients/${recipientId}/complete`, payload);
  }

  resendRecipientEmail(recipientId: number, payload: { sent_by?: number } = {}): Observable<any> {
    return this.http.post(`${this.apiUrl}/recipients/${recipientId}/resend-email`, payload);
  }

  getProofPdfUrl(recipientId: number) {
    return `${this.apiUrl}/recipients/${recipientId}/proof/pdf`;
  }
}
